import { TopBarRoute, ListView } from "../../components/templates";
import { Stack, Title, Card, Icon, Tag, Drawer } from "@introist/react-foundation/v2";
import { useUsers } from "../hooks/use-users";
import { Skeletons } from "../../components/molecules";
import { List, ListRow } from "../../components/organisms";
import { UserRole } from "../api";
import { useParams, useNavigate } from "react-router-dom";
import { UserEditor } from "./UserEditor";
import { UserIdentityIcons } from "../components/UserIdentityIcons";

const getRoleVariant = (role: UserRole) => {
  switch (role) {
    case "owner":
      return "purple" as const;
    case "admin":
      return "info" as const;
    case "user":
    default:
      return "outlined" as const;
  }
};

const getRoleDisplayName = (role: UserRole) => {
  switch (role) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "user":
    default:
      return "User";
  }
};

export const UsersRoute = () => {
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();

  return (
    <TopBarRoute
      paths={[
        {
          key: "users",
          title: "Users"
        }
      ]}
    >
      <Stack vertical>
        <ListView.Header title="Users" onCreate={() => navigate("/aistudio/users/new")} />
        <UsersListContent />
      </Stack>

      {/* User Editor Drawer */}
      <Drawer open={!!userId} onClose={() => navigate("/aistudio/users")} width="540px">
        {userId && (
          <UserEditor
            userId={userId === "new" ? undefined : userId}
            onClose={() => navigate("/aistudio/users")}
          />
        )}
      </Drawer>
    </TopBarRoute>
  );
};

const UsersListContent = () => {
  const { data: users = [], isLoading } = useUsers();
  const navigate = useNavigate();

  if (isLoading) {
    return <Skeletons.List rowCount={3} rowHeight={60} />;
  }

  if (users.length === 0) {
    return (
      <Card>
        <Stack
          vertical
          style={{
            alignItems: "center",
            gap: "var(--spacing-small)",
            padding: "var(--spacing-large)"
          }}
        >
          <Icon name="person" dimmed />
          <Title style={{ color: "var(--palette-foreground-subdued)" }}>No users found</Title>
          <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
            Users with access to this workspace will appear here
          </Title>
        </Stack>
      </Card>
    );
  }

  return (
    <List>
      {users.map(user => {
        const displayName =
          user.firstname || user.lastname
            ? `${user.firstname || ""} ${user.lastname || ""}`.trim()
            : user.email;

        return (
          <ListRow
            key={user.id}
            gridTemplateColumns="auto auto 1fr auto"
            onClick={() => navigate(`/aistudio/users/${user.id}`)}
            clickable
          >
            <Icon name="person" />
            <Stack vertical style={{ gap: "var(--spacing-xxSmall)" }}>
              <Title variant="bold">{displayName}</Title>
              {(user.firstname || user.lastname) && (
                <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
                  {user.email}
                </Title>
              )}
            </Stack>
            <Tag colorVariant={getRoleVariant(user.role)}>{getRoleDisplayName(user.role)}</Tag>
            <UserIdentityIcons identities={user.identities} />
          </ListRow>
        );
      })}
    </List>
  );
};
